import React, { useState, useCallback, useEffect } from 'react';

import { useNavigate, Link, useLocation } from 'react-router-dom';

import Form, { Item, Label, RequiredRule } from 'devextreme-react/form';
import LoadIndicator from 'devextreme-react/load-indicator';
import notify from 'devextreme/ui/notify';

import { useAuth } from '../../../contexts/auth';
import { startPayment } from '../../../api/registration';

import './LoginForm.scss';

export const LoginForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(loadFormData());

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('activated') === 'true') {
      notify('Uw account is geactiveerd! U kunt nu inloggen.', 'success', 5000);
    }
    if (params.get('subscription') === 'active') {
      notify('Betaling ingesteld! Controleer uw e-mail om uw account te activeren als u dat nog niet heeft gedaan.', 'success', 6000);
    }
  }, [location.search]);

  const onSubmit = useCallback(
      async(e) => {
      e.preventDefault();
      setLoading(true);
      const result = await signIn(formData.email, formData.password);
      if (result.isOk) {
        navigate('/');
      } else if (result.paywall) {
        // Trial expired — send the user to the Revolut checkout to reactivate.
        notify(result.message || 'Uw proefperiode is verlopen. U wordt doorgestuurd naar de betaalpagina...', 'warning', 5000);
        try {
          const pay = await startPayment(formData.email);
          if (pay.PaymentUrl) {
            window.location.href = pay.PaymentUrl;
            return;
          }
          notify(pay.Message || 'Betaalpagina kon niet worden geopend.', 'error', 4000);
        } catch {
          notify('Betaalpagina kon niet worden geopend. Probeer het later opnieuw.', 'error', 4000);
        }
        setLoading(false);
      } else {
        setLoading(false);
        notify(result.message, 'error', 2000);
      }
    },
    [signIn, formData]
  );

    function loadFormData() {
        if ('authObject' in localStorage) {
            const storedData = localStorage.getItem('authObject');
            return storedData ? JSON.parse(storedData) : null;
        }
        else {
            return { email: '', password: '', rememberMe: false };
        }
    }

    const onFieldDataChanged = (e) => {
        switch (e.dataField) {
            case 'rememberMe':
                if (e.value) {
                    localStorage.setItem('authObject', JSON.stringify(formData));
                }
                else {
                    localStorage.removeItem('authObject');
                }
                break;
            case 'email':
                setFormData(prevState => {
                    return { ...prevState, emmail: e.value };
                });
                if (formData.rememberMe) { localStorage.setItem('authObject', JSON.stringify(formData)); }
                break;
            case 'password':
                setFormData(prevState => {
                    return { ...prevState, password: e.value };
                });
                if (formData.rememberMe) { localStorage.setItem('authObject', JSON.stringify(formData)); }
                break;
        }
    };

  return (
    <form className='login-form' onSubmit={onSubmit}>
          <Form
        formData={formData}
        disabled={loading}
        showColonAfterLabel={false}
        showRequiredMark={false}
        onFieldDataChanged={onFieldDataChanged}
      >
        <Item dataField='email' editorType='dxTextBox' editorOptions={emailEditorOptions}>
          <RequiredRule message='Email is required' />
          <Label text='E-mailadres' />
        </Item>
        <Item dataField='password' editorType='dxTextBox' editorOptions={passwordEditorOptions}>
          <Label text='Wachtwoord' />
        </Item>
      </Form>
      <div className='remember-row'>
        <label className='form-text' style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
          <input
            type='checkbox'
            checked={formData.rememberMe}
            onChange={(e) => {
              const val = e.target.checked;
              setFormData(prev => ({ ...prev, rememberMe: val }));
              if (val) { localStorage.setItem('authObject', JSON.stringify({ ...formData, rememberMe: val })); }
              else { localStorage.removeItem('authObject'); }
            }}
          />
          Ik wil ingelogd blijven
        </label>
        <Link to='/forgot-password' className='forgot-link'>Wachtwoord vergeten?</Link>
      </div>
      <button
        type='submit'
        disabled={loading}
        className='login-submit-btn'
        style={{
          width: '100%',
          height: '42px',
          borderRadius: '8px',
          border: 'none',
          background: '#ef4444',
          color: '#fff',
          fontSize: '15px',
          fontWeight: 600,
          cursor: loading ? 'not-allowed' : 'pointer',
          opacity: loading ? 0.7 : 1,
          transition: 'background 0.15s',
        }}
      >
        {loading ? <LoadIndicator width='24px' height='24px' visible /> : 'Log in'}
      </button>
    </form>
  );
};

const emailEditorOptions = { stylingMode: 'outlined', placeholder: 'naam@voorbeeld.nl', mode: 'text', height: 42 };
const passwordEditorOptions = { stylingMode: 'outlined', placeholder: '••••••••', mode: 'password', height: 42 };
